import { Badge, Tooltip, Flex } from "@radix-ui/themes";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  PauseCircle,
} from "lucide-react";

interface SubscriptionStatusBadgeProps {
  status: string;
  size?: "1" | "2" | "3";
  showIcon?: boolean;
}

export function SubscriptionStatusBadge({
  status,
  size = "2",
  showIcon = false,
}: SubscriptionStatusBadgeProps) {
  const statusConfig = getStatusConfig(status);

  const badge = (
    <Badge color={statusConfig.color} size={size} variant="soft">
      <Flex align="center" gap="1">
        {showIcon && statusConfig.icon}
        {statusConfig.label}
      </Flex>
    </Badge>
  );

  if (statusConfig.description) {
    return <Tooltip content={statusConfig.description}>{badge}</Tooltip>;
  }

  return badge;
}

function getStatusConfig(status: string) {
  const normalizedStatus = status.toLowerCase();

  switch (normalizedStatus) {
    case "active":
      return {
        color: "green" as const,
        label: "Active",
        description: "Subscription is active and will renew automatically",
        icon: <CheckCircle2 size={12} />,
      };
    case "trialing":
      return {
        color: "blue" as const,
        label: "Trialing",
        description: "Subscription is in trial period",
        icon: <Clock size={12} />,
      };
    case "past_due":
      return {
        color: "orange" as const,
        label: "Past Due",
        description:
          "Payment failed. Subscription will be canceled if not paid soon",
        icon: <AlertCircle size={12} />,
      };
    case "canceled":
    case "cancelled":
      return {
        color: "red" as const,
        label: "Canceled",
        description: "Subscription has been canceled",
        icon: <XCircle size={12} />,
      };
    case "unpaid":
      return {
        color: "red" as const,
        label: "Unpaid",
        description: "Subscription payment failed and is unpaid",
        icon: <XCircle size={12} />,
      };
    case "incomplete":
      return {
        color: "gray" as const,
        label: "Incomplete",
        description:
          "Subscription setup is incomplete. Payment method required",
        icon: <PauseCircle size={12} />,
      };
    case "incomplete_expired":
      return {
        color: "gray" as const,
        label: "Incomplete Expired",
        description: "Subscription setup expired without payment",
        icon: <XCircle size={12} />,
      };
    default:
      return {
        color: "gray" as const,
        label: status,
        description: undefined,
        icon: undefined,
      };
  }
}
